import React from 'react';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';

import {
  renderLicenseText, renderNotice, type LicenseData, type LicenseItem,
} from './licenses';

export type Props = {
  open: boolean,
  onClose: () => void,
};

const LicenseDialog: React.FC<Props> = ({ open, onClose }) => {
  const [data, setData] = React.useState<LicenseData | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [onlyEssential, setOnlyEssential] = React.useState(true);
  const [detail, setDetail] = React.useState<LicenseItem | null>(null);

  React.useEffect(() => {
    let ignore = false;
    async function load() {
      setError(null);
      try {
        const res = await fetch('/licenses.json', { cache: 'no-store' });
        if (!res.ok) throw new Error(`${res.status}`);
        const json = (await res.json()) as LicenseData;
        if (!ignore) setData(json);
      } catch (e: any) {
        if (!ignore) setError('ライセンス情報を取得できませんでした。後ほど再度お試しください。');
      }
    }
    if (open && !data && !error) {
      load();
    }
    return () => { ignore = true; };
  }, [data, error, open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ textAlign: 'center' }}>
        ライセンス情報（サードパーティ）
      </DialogTitle>
      <DialogContent>
        {!data && !error && (
          <Typography variant="body2">読み込み中...</Typography>
        )}
        {error && (
          <Typography variant="body2" color="error">{error}</Typography>
        )}
        {data && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              生成日時:
              {' '}
              {new Date(data.generatedAt).toLocaleString()}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                variant={onlyEssential ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setOnlyEssential(true)}
              >
                重要ライセンスのみ
              </Button>
              <Button
                variant={!onlyEssential ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setOnlyEssential(false)}
              >
                全件表示
              </Button>
              <Typography variant="caption" color="text.secondary">
                MIT/BSD/Apache-2.0 は全文掲示 + NOTICE を表示
              </Typography>
            </Box>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>パッケージ</TableCell>
                    <TableCell>バージョン</TableCell>
                    <TableCell>ライセンス</TableCell>
                    <TableCell>リンク</TableCell>
                    <TableCell align="right">詳細</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(onlyEssential
                    ? data.packages.filter((p) => /MIT|BSD|Apache-2\.0/i.test(p.license || ''))
                    : data.packages
                  ).map((p) => (
                    <TableRow key={`${p.name}@${p.version}`}>
                      <TableCell>{p.name}</TableCell>
                      <TableCell>{p.version}</TableCell>
                      <TableCell>{p.license || '-'}</TableCell>
                      <TableCell>
                        {p.homepage && (
                          <>
                            <Link href={p.homepage} target="_blank" rel="noreferrer">homepage</Link>
                            {' '}
                          </>
                        )}
                        {p.repository && (
                          <Link href={p.repository.replace(/^git\+/, '')} target="_blank" rel="noreferrer">repo</Link>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {/MIT|BSD|Apache-2\.0/i.test(p.license || '') && (
                          <Button size="small" onClick={() => setDetail(p)}>表示</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center' }}>
        <Button onClick={onClose}>閉じる</Button>
      </DialogActions>
      {detail && (
        <Dialog open onClose={() => setDetail(null)} fullWidth maxWidth="md">
          <DialogTitle sx={{ textAlign: 'center' }}>
            {`${detail.name}@${detail.version}`}
            {' '}
            のライセンス
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 1 }}>
              種別:
              {detail.license || '-'}
            </Typography>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>LICENSE</Typography>
            <Box
              component="pre"
              sx={{
                whiteSpace: 'pre-wrap', p: 1, bgcolor: '#fafafa', borderRadius: 1, maxHeight: 360, overflow: 'auto',
              }}
            >
              {renderLicenseText(detail)}
            </Box>
            {renderNotice(detail)}
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center' }}>
            <Button onClick={() => setDetail(null)}>閉じる</Button>
          </DialogActions>
        </Dialog>
      )}
    </Dialog>
  );
};

export default LicenseDialog;
